const INITIAL_STATE = {
  unReadCount: 0,
  notifications: [],
  // boolean
  showNotificationPanel: false,

  filterOptions: {
    searchValue: '',
    isUnRead: false,
    selectedType: 'all',
    selectedSubTypes: []
  },

  pagination: {
    offset: 0,
    limit: 20,
    hasMore: true,
    isLoading: false
  }
};

const filterNotifications = (notifications, filterOptions) => {
  const idList = [];
  const filteredNotifications = notifications
    .filter((notification) => {
      if (idList.includes(notification.id)) {
        return false;
      }

      idList.push(notification.id);

      if (
        filterOptions.selectedSubTypes.length > 0 &&
        !filterOptions.selectedSubTypes.includes(notification.subType)
      ) {
        return false;
      }

      if (filterOptions.isUnRead && notification.recipients?.[0]?.isRead) {
        return false;
      }

      if (filterOptions.searchValue.trim().length > 0) {
        const normalize = (value) =>
          String(value ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .replace(/\s+/g, ' ')
            .trim();

        const searchValue = normalize(filterOptions.searchValue);
        const titleMatch = normalize(notification.metadata?.title).includes(searchValue);
        const descriptionMatch = normalize(notification.metadata?.description).includes(searchValue);
        const partnershipTitleMatch = normalize(notification.metadata?.partnershipTitle).includes(searchValue);
        const employeeMatch = normalize(notification.sentByEmployee?.fullName).includes(searchValue);
        const clientMatch = normalize(notification.sentByClient?.title).includes(searchValue);
        const creatorMatch = normalize(notification.sentByCreator?.fullName).includes(searchValue);
        const otherPartyMatch = normalize(notification.metadata?.otherParty?.name).includes(searchValue);

        if (!titleMatch && !descriptionMatch && !employeeMatch && !clientMatch && !creatorMatch && !partnershipTitleMatch && !otherPartyMatch) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => b.createdAt - a.createdAt);
  return filteredNotifications;
};

const actionTypes = {
  UPDATE_STATE: 'UPDATE_STATE',
  COUNT_UPDATED: 'COUNT_UPDATED',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  FILTER_OPTIONS_UPDATED: 'FILTER_OPTIONS_UPDATED',
  APPEND_NOTIFICATIONS: 'APPEND_NOTIFICATIONS',
  RESET_PAGINATION: 'RESET_PAGINATION',
  UPDATE_PAGINATION: 'UPDATE_PAGINATION'
};

const commonAction = (key, data) => {
  return {
    type: actionTypes.UPDATE_STATE,
    payload: { [key]: data }
  };
};

const actions = {
  setFilterOptions: (filterOptions) => {
    return {
      type: actionTypes.FILTER_OPTIONS_UPDATED,
      payload: filterOptions
    };
  },
  updateUnReadCount: (payload) => {
    return {
      type: actionTypes.COUNT_UPDATED,
      payload
    };
  },
  addNotification: (notification) => {
    return {
      type: actionTypes.ADD_NOTIFICATION,
      payload: notification
    };
  },
  removeNotification: (notification) => {
    return {
      type: actionTypes.REMOVE_NOTIFICATION,
      payload: notification
    };
  },
  updateNotification: (notification) => {
    return {
      type: actionTypes.UPDATE_NOTIFICATION,
      payload: notification
    };
  },
  setNotifications: (notifications) => {
    return commonAction('notifications', notifications);
  },
  setShowNotificationPanel: (showNotificationPanel) => {
    if (showNotificationPanel) {
      return commonAction('showNotificationPanel', showNotificationPanel);
    }
    return {
      type: actionTypes.UPDATE_STATE,
      payload: {
        showNotificationPanel: false,
        notifications: [],
        filterOptions: {
          searchValue: '',
          isUnRead: false,
          selectedType: 'all',
          selectedSubTypes: []
        },
        pagination: {
          offset: 0,
          limit: 20,
          hasMore: true,
          isLoading: false
        }
      }
    }
  },
  appendNotifications: (notifications, hasMore) => {
    return {
      type: actionTypes.APPEND_NOTIFICATIONS,
      payload: { notifications, hasMore }
    };
  },
  resetPagination: () => {
    return {
      type: actionTypes.RESET_PAGINATION
    };
  },
  updatePagination: (pagination) => {
    return {
      type: actionTypes.UPDATE_PAGINATION,
      payload: pagination
    };
  }
};

const reducer = (state = INITIAL_STATE, actions) => {
  switch (actions.type) {
    case actionTypes.FILTER_OPTIONS_UPDATED:
      return {
        ...state,
        filterOptions: { ...state.filterOptions, ...actions.payload },
        pagination: INITIAL_STATE.pagination
      };
    case actionTypes.COUNT_UPDATED:
      if ('count' in actions.payload) {
        return { ...state, unReadCount: actions.payload.count };
      } else if ('add' in actions.payload) {
        return { ...state, unReadCount: state.unReadCount + actions.payload.add };
      } else if ('remove' in actions.payload) {
        return { ...state, unReadCount: state.unReadCount - actions.payload.remove };
      }
      return state;
    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        unReadCount: state.unReadCount + 1,
        notifications: filterNotifications(
          [actions.payload, ...state.notifications],
          state.filterOptions
        )
      };
    case actionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        unReadCount: state.unReadCount - 1,
        notifications: state.notifications.filter(
          (notification) => notification.id !== actions.payload.id
        )
      };
    case actionTypes.UPDATE_NOTIFICATION:
      return {
        ...state,
        notifications: filterNotifications(
          state.notifications.map((notification) =>
            notification.id === actions.payload.id ? actions.payload : notification
          ),
          state.filterOptions
        )
      };
    case actionTypes.APPEND_NOTIFICATIONS:
      return {
        ...state,
        notifications: filterNotifications(
          [...state.notifications, ...actions.payload.notifications],
          state.filterOptions
        ),
        pagination: {
          ...state.pagination,
          offset: state.pagination.offset + actions.payload.notifications.length,
          hasMore: actions.payload.hasMore,
          isLoading: false
        }
      };
    case actionTypes.RESET_PAGINATION:
      return {
        ...state,
        notifications: [],
        pagination: {
          ...INITIAL_STATE.pagination,
          isLoading: true
        }
      };
    case actionTypes.UPDATE_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...actions.payload
        }
      };
    case actionTypes.UPDATE_STATE:
      return { ...state, ...actions.payload };

    default:
      return state;
  }
};

export {
  actions as notificationPanelActions,
  reducer as notificationPanelReducer,
  INITIAL_STATE as notificationPanelInitialState
};
